<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { buildMeta } from "~~/utils/meta-builder";

const config = useRuntimeConfig()
const mode = (config.public.blog?.mode || 'published') as 'draft' | 'published';
const route = useRoute()
const i18n = useI18n()

const lang = computed(() => i18n.locale.value === 'en' ? 'default' : i18n.locale.value)

const i18nHead = useLocaleHead({
	addSeoAttributes: true
}) 

const getSlug = (item: any): string => {
	if (!item) return ''
	const fullSlug = item.full_slug as string
	return `/${fullSlug}`;
}

// DIREKTE STORYBLOK API-CALLS ohne Module
const fetchStoryblokData = async (params: any) => {
  const token = 'ZKVz5vYrHivRPiqP6HcOdwtt' // Dein Token direkt
  const baseUrl = 'https://api.storyblok.com/v2/cdn/stories' // RICHTIGE URL (nicht api-eu)
  
  const queryParams = new URLSearchParams({
    token,
    version: params.version || 'published',
    starts_with: params.starts_with || '',
    language: params.language || 'default'
  })
  
  // Resolve relations nur wenn angegeben
  if (params.resolve_relations) {
    queryParams.append('resolve_relations', params.resolve_relations)
  }
  
  const url = `${baseUrl}?${queryParams}`
  console.log('🔍 Fetching:', url)
  
  try {
    const response:any = await $fetch(url)
    console.log('📊 Response structure:', {
      hasStories: !!response.stories,
      storiesCount: response.stories?.length || 0,
      hasRels: !!response.rels,
      relsCount: response.rels?.length || 0,
      firstStoryCategories: response.stories?.[0]?.content?.category?.length || 0
    })
    return { data: response }
  } catch (error) {
    console.error('❌ API Error:', error)
    throw error
  }
}

console.log('🔄 Loading blog data directly...')

// DIREKTE API-CALLS - RICHTIG für Blog-Seite (nicht für einzelne Posts)
const [catsResponse, postsResponse] = await Promise.all([
  fetchStoryblokData({
    version: mode,
    starts_with: 'blog',
    language: lang.value
  }),
  fetchStoryblokData({
    version: mode,
    starts_with: 'posts',
    language: lang.value,
    resolve_relations: 'category,author,reviewer' // Relations für Posts
  })
])

console.log('✅ Categories loaded:', catsResponse.data?.stories?.length || 0)
console.log('✅ Posts loaded:', postsResponse.data?.stories?.length || 0)
console.log('✅ Relations loaded:', postsResponse.data?.rels?.length || 0)

const blogCategories = catsResponse.data?.stories || []
const blogPosts = postsResponse.data?.stories || []
const blogRels = postsResponse.data?.rels || [] // Relations aus Posts-Response

const categoryAll = {
	content: { name: 'All' },
	full_slug: '/blog/all',
	slug: 'all'
}

const categories = computed(() => {
  console.log('Categories computed:', blogCategories.length)
  return [categoryAll, ...blogCategories]
})

const enSlug = ref('/blog')
const deSlug = ref('/de/blog')

const stories = computed(() => {
	let list = [...blogPosts] // Copy array
	console.log('Stories computed:', list.length)
	
	// Resolve categories mit Relations
	for (const story of list) {
		const resolvedCategories: any[] = []
		
		if (story.content?.category) {
			for (const categoryId of story.content.category) {
				// Suche zuerst in Relations
				let foundCategory = blogRels.find((rel:any) => rel.uuid === categoryId)
				
				// Fallback: Suche in Kategorien
				if (!foundCategory) {
					foundCategory = categories.value.find(category => category.uuid === categoryId)
				}
				
				if (foundCategory) {
					resolvedCategories.push(foundCategory)
				} else {
					console.warn('Category not found:', categoryId)
				}
			}
			story.content.categories = resolvedCategories
			
			// Debug für ersten Post
			if (list.indexOf(story) === 0) {
				console.log('First post categories:', {
					categoryIds: story.content.category,
					resolvedCount: resolvedCategories.length,
					categoryNames: resolvedCategories.map(c => c.content?.name)
				})
			}
		}
	}
	
	return list
})

const featured = computed(() => {
  const result = stories.value.filter((s: any) => s.content?.featured)
  console.log('Featured computed:', result.length)
  return result
})

const filtered = ref<any[]>([])

const filterByCategory = () => {
	const categorySlug: string = (route.query.category as string) || 'all'
	
	console.log('=== FILTERING ===')
	console.log('Category slug:', categorySlug)
	console.log('Total posts:', stories.value.length)

	const filterByAll = categorySlug === 'alle' || categorySlug === 'all' || categorySlug === ''

	const list = stories.value.filter((s: any) => {
		// Exclude featured posts
		if (s.content?.featured) return false
		
		// If filtering by all, include all non-featured
		if (filterByAll) return true
		
		// Check if post has the selected category
		return s.content?.categories?.some((c: any) => 
			translatedPath(c.full_slug) === categorySlug
		)
	})
	
	console.log('Filtered posts:', list.length)
	filtered.value = list
	
	// Update SEO links
	const currentCategory = categories.value.find((x: any) => 
		x.slug === categorySlug || translatedPath(x.full_slug) === categorySlug
	)
	
	const de = currentCategory?.translated_slugs?.find((x: any) => x.lang === 'de')?.path.split('/')[1] || 'all'
	
	enSlug.value = '/blog?category=' + (currentCategory?.slug || 'all')
	deSlug.value = '/de/blog?category=' + de 

	const deUrl = config.public.hostname + deSlug.value
	const enUrl = config.public.hostname + enSlug.value

	useHead(buildMeta({
		key: 'blog', 
		follow: true,
		localeLinks: [
			{ rel: 'alternate', href: enUrl, hreflang: 'en' },
			{ rel: 'alternate', href: enUrl, hreflang: 'en-US' },
			{ rel: 'alternate', href: enUrl, hreflang: 'x-default' },
			{ rel: 'alternate', href: deUrl, hreflang: 'de' },
			{ rel: 'alternate', href: deUrl, hreflang: 'de-DE' },
			{ rel: 'canonical', href: config.public.hostname + route.fullPath },
		]
	}, i18n, i18nHead))
}

// Watchers
watch(() => route.query, async () => {
	console.log('Query changed:', route.query.category)
	await nextTick()
	filterByCategory()
}, { immediate: true })

// Initialize on mount
onMounted(async () => {
	console.log('=== BLOG MOUNTED ===')
	console.log('Categories:', categories.value.length)
	console.log('Posts:', stories.value.length)
	console.log('Featured:', featured.value.length)
	
	await nextTick()
	filterByCategory()
	
	console.log('=== MOUNT COMPLETE ===')
})

const isCurrentCategory = (slug: string): boolean => {
	if (!slug) return false
	const categorySlug = route.query.category || (i18n.locale.value === 'de' ? 'alle' : 'all')
	return translatedPath(slug) === categorySlug
}

const categoryQuery = (slug: string) => {
	return 'blog?category=' + translatedPath(slug)
}

// Debug info
console.log('=== INITIAL SETUP ===')
console.log('Categories loaded:', blogCategories.length)
console.log('Posts loaded:', blogPosts.length)
console.log('Categories computed:', categories.value.length)
console.log('Stories computed:', stories.value.length)
console.log('Featured computed:', featured.value.length)
</script>

<template>
	<div class="bg-newdark-800 text-white">
		<Header theme="dark" />
	
	
		<div class="w-full max-w-screen-xl mx-auto px-10">

				<!-- Featured Posts -->
				<PostItemFeatured 
					v-for="item in featured" 
					:key="item.id" 
					:blok="item.content" 
					:slug="getSlug(item)" 
				/>

			<NuxtLayout>
				<!-- Category Filter -->
				<div class="flex flex-wrap justify-center lg:justify-start gap-x-2 md:gap-x-6 gap-y-6 pt-16 overflow-x-scroll">
					<NuxtLink  
						v-for="category in categories" 
						:key="category.slug || category.uuid || category.id"
						:to="categoryQuery(category.full_slug)"
						class="px-6 py-2 hover:text-white transition-colors" 
						:class="{
							'text-newdark-800 bg-white rounded-full hover:bg-white/90': isCurrentCategory(category.full_slug),
							'text-white/70 hover:text-white': !isCurrentCategory(category.full_slug)
						}" 
					>
						{{ category.content.name }}
					</NuxtLink>
				</div>

				<!-- Posts Grid -->
				<div class="gap-8 pt-16 masonry sm:masonry-sm md:masonry-md"> 
					<transition-group name="list">
						<PostItem 
							v-for="item in filtered" 
							:key="item.id" 
							:blok="item.content" 
							:slug="getSlug(item)" 
						/>
					</transition-group>
				</div>

			
			</NuxtLayout>
		</div>

		<Footer theme="dark" :de-slug="deSlug" :en-slug="enSlug" />
	</div>
</template>

<style scoped>
.list-move,
.list-enter-active,
.list-leave-active {
  transition: all .5s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
	transform: translateY(30px);
}
.list-leave-active {
  position: absolute;
	max-width: 300px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>